/**
 * 砺锋台 V6.011 — 真实关卡数据驱动棋盘
 *
 * 布局：左栏（身份卡 + 排行榜） / 右栏（战术地图 + 战斗按钮）
 * 路由：#/sharpen3
 *
 * V6.011 改动：
 * - 棋盘从硬编码 12×18 mock → 16×11 数据驱动（63 关，按 max_units_per_team 分类）
 * - 关卡位置哈希种子稳定分配，每页 30 关 + 地形填充
 * - 分页器在战区标牌旁
 * - inline 战斗观看接入（battle-viewer mock 阶段）
 * - Tab 新增 2V2（大军团保留 locked 占位）
 */

import { TacticalMap } from "../render/sharpen-v3/tactical-map";
import type { BoardPiece } from "../render/sharpen-v3/tactical-map";
import type { OpenType, TypeProgress, BattleType, LevelEntry, BoardPage, RankId, BattleRecord, RecordField } from "../render/sharpen-v3/types";
import { TYPE_COLORS, TYPE_LABELS, RANK_CONFIGS, RANK_PROMOTION_THRESHOLD } from "../render/sharpen-v3/types";
import { loadState, saveState } from "../render/sharpen-v3/progress-store";
import { levelProgress } from "../render/sharpen-v3/level-system";
import { renderMedalSvg } from "../render/sharpen-v3/medal-render";
import { loadAllLevels, getLastLevelLoadStatus } from "../render/sharpen-v3/level-loader";
import { generateBoardPage, computeTotalPages } from "../render/sharpen-v3/board-generator";
import { startBattleViewer } from "../render/sharpen-v3/battle-viewer";
import { pathFor } from "../router/screen-router";
import { renderHeaderHTML, bindHeaderEvents } from "../components/header-bar";
import { isLoggedIn } from "../core/user-context";

// ── 状态 ──
let sharpenState = loadState();
let tacticalMap: TacticalMap | null = null;
/** go() 回调（模块级，供 rebuildBoardUI 复用） */
let sharpenGo: (path: string) => void = (path: string) => { window.location.hash = path; };

/** V6-011 关卡数据缓存（加载后复用） */
let allLevelsCache: Record<OpenType, LevelEntry[]> | null = null;

/** V6-011 棋盘分页状态（每次切 Tab 回到第 1 页，不持久化） */
let boardPageIdx: Record<OpenType, number> = { "1v1": 0, "2v2": 0, "3v3": 0 };

// ── 排行榜分页状态 ──
const RANKING_PAGE_SIZE = 20;
const RANKING_TOTAL = 1000;
let rankingPage = 0;
const MAX_RANKING_PAGE = Math.ceil(RANKING_TOTAL / RANKING_PAGE_SIZE) - 1;

// ── V6.011 Tab 配置（新增 2V2，大军团保留 locked 占位）──
interface SharpenTab {
  label: string;
  type: BattleType;
  locked?: boolean;
}

const SHARPEN_TABS: SharpenTab[] = [
  { label: "1V1", type: "1v1" },
  { label: "2V2", type: "2v2" },
  { label: "3V3", type: "3v3" },
  { label: "大军团", type: "large", locked: true },
];

// ── Mock 排行榜数据 ──

// 用伪随机种子保持数据稳定
function pseudoRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

interface RankEntry {
  rank: number;
  name: string;
  cleared: number;
}

function generateMockRanking(): RankEntry[] {
  const rand = pseudoRandom(42);
  const entries: RankEntry[] = [];

  // 名字词库（承刃者代号）
  const prefixes = ["影", "刃", "锋", "霜", "炽", "星", "雷", "雾", "铁", "风",
                    "寒", "炎", "黯", "辉", "渊", "岚", "钢", "冥", "烈", "朔"];
  const suffixes = ["镰", "盾", "矛", "戟", "爪", "翼", "尾", "角", "牙", "心",
                    "锤", "弓", "匕", "钩", "斧", "锯", "链", "笛", "钟", "棺"];

  for (let i = 0; i < RANKING_TOTAL; i++) {
    const rank = i + 1;
    // cleared: 高段位多，低段位少，带随机扰动
    let maxCleared = 0;
    if (rank <= 100) maxCleared = 72;
    else if (rank <= 300) maxCleared = 60;
    else if (rank <= 600) maxCleared = 40;
    else maxCleared = 20;
    const cleared = Math.max(0, Math.floor(maxCleared - (rank - 1) * 0.06 * (1 + rand())));
    // 名字生成
    const pi = Math.floor(rand() * prefixes.length);
    const si = Math.floor(rand() * suffixes.length);
    const name = `${prefixes[pi]}${suffixes[si]}-${String(rank).padStart(4, "0")}`;
    entries.push({ rank, name, cleared });
  }

  return entries;
}

const MOCK_RANKING = generateMockRanking();

export function renderScreen10Sharpen(container: HTMLElement, go?: (path: string) => void): void {
  if (go) sharpenGo = go;
  if (!isLoggedIn()) {
    renderSharpenLoginRequired(container);
    return;
  }
  sharpenState = loadState();
  buildDOM(container);
  void initBoard();
}

function renderSharpenLoginRequired(container: HTMLElement): void {
  container.innerHTML = /* html */ `
    <div class="s10-sharpen s10-auth-required">
      ${renderHeaderHTML({ backPath: pathFor("armory-hall"), title: "砺 锋 台", subtitle: "FORGE · QUENCH", statusText: "AUTH REQUIRED" })}
      <div class="s10-auth-panel">
        <div class="s10-auth-code">AUTH / 401</div>
        <h2>请先登录</h2>
        <p>砺锋台进度属于账号数据。未登录状态不再创建或恢复匿名进度。</p>
        <button id="s10-auth-login" type="button">返回首页登录</button>
      </div>
    </div>`;
  bindHeaderEvents(sharpenGo, pathFor("armory-hall"));
  container.querySelector("#s10-auth-login")?.addEventListener("click", () => sharpenGo("#/"));
}

// ── DOM 构建 ──
function buildDOM(container: HTMLElement): void {
  const mode = sharpenState.lastSelectedType as OpenType;
  const progress = sharpenState.typeProgress[mode];
  const lp = levelProgress(progress.exp);
  const rankConfig = RANK_CONFIGS.find((r) => r.id === progress.currentRank) ?? RANK_CONFIGS[0];
  // V6-011: 关卡总数从缓存读取（initBoard 后填充）
  const totalLevels = allLevelsCache?.[mode]?.length ?? 0;
  const currentTab = SHARPEN_TABS.find((t) => t.type === mode) ?? SHARPEN_TABS[0];

  container.innerHTML = /* html */ `
  <div class="s10-sharpen">
    ${renderHeaderHTML({
      backPath: pathFor("armory-hall"),
      title: "砺 锋 台",
      subtitle: "FORGE · QUENCH",
      statusText: "ONLINE ●  SYS:ARMED",
    })}

    <!-- 主体 -->
    <div class="s10-main">
      <!-- 左栏：上身份卡 + 下排行榜 -->
      <div class="s10-left">
        <!-- 身份卡 -->
        <div class="s10-identity">
          <!-- Tab 页 -->
          <div class="s10-id-tabs" id="s10-id-tabs">
            ${SHARPEN_TABS.map((t) => {
              const isActive = t.type === currentTab.type;
              const isLocked = !!t.locked;
              return `
              <button class="s10-id-tab ${isActive ? "s10-id-tab--active" : ""} ${isLocked ? "s10-id-tab--locked" : ""}"
                      data-mode="${t.type}"
                      ${isLocked ? "disabled" : ""}
                      style="${isActive ? `color:${TYPE_COLORS[t.type]};border-color:${TYPE_COLORS[t.type]};` : ""}">
                ${t.label}${isLocked ? " 🔒" : ""}
              </button>`;
            }).join("")}
          </div>
          <!-- 勋章 + 信息 -->
          <div class="s10-id-body">
            <div class="s10-medal-wrap" id="s10-medal">
              <span class="s10-medal-glow"></span>
              ${renderMedalSvg(lp.currentLevel, mode)}
            </div>
            <div class="s10-info">
              <div class="s10-type-label" style="color:${TYPE_COLORS[mode]};">
                ${TYPE_LABELS[mode]}
              </div>
              <div class="s10-level-text">Lv.${lp.currentLevel}</div>
              <div class="s10-exp-bar">
                <div class="s10-exp-fill" style="width:${Math.round(lp.progress * 100)}%;background:${TYPE_COLORS[mode]};"></div>
              </div>
              <div class="s10-exp-text">
                EXP ${lp.expInCurrentLevel}/${lp.expNeededForNext} · NEXT ${lp.expToNext}
              </div>
              <div class="s10-rank-text">RANK · ${rankConfig.displayName}</div>
              <div class="s10-nexus-text">NEXUS: 0x4ECD...</div>
              <div class="s10-stats-row" id="s10-stats-row">
                <span>通过 <b>${progress.clearedLevels.length}</b>/<b id="s10-identity-total">${totalLevels || "—"}</b></span>
                <span>胜率 <b>${calcWinRate(progress)}%</b></span>
                <span>连胜 <b>${progress.personalBests?.currentStreak ?? 0}</b></span>
                <span>最长连胜 <b>${progress.personalBests?.longestStreak ?? 0}</b></span>
                <span>均胜率 <b>${calcAvgWinRate(progress)}%</b></span>
              </div>
            </div>
          </div>
        </div>

        <!-- P0-03 最近战报（对比箭头） -->
        <div class="s10-recent-battles" id="s10-recent-battles">
          ${renderRecentBattlesHTML(progress)}
        </div>

        <!-- 排行榜 -->
        <div class="s10-leaderboard">
          <div class="s10-lb-header">
            <span>RANKING · 承刃者天梯</span>
            <span>TOP 1000</span>
          </div>
          <!-- 表头 -->
          <div class="s10-lb-table-header">
            <span class="s10-lb-col-rank">排序</span>
            <span class="s10-lb-col-name">名称</span>
            <span class="s10-lb-col-cleared">通关场数</span>
          </div>
          <!-- 滚动列表 -->
          <div class="s10-lb-list" id="s10-lb-list">
            ${buildRankingTableHTML(rankingPage)}
          </div>
          <!-- 分页器 -->
          <div class="s10-lb-pagination" id="s10-lb-pagination">
            <button class="s10-lb-page-btn" id="s10-lb-prev" ${rankingPage === 0 ? "disabled" : ""}>← 上一页</button>
            <span class="s10-lb-page-info">${rankingPage + 1} / ${MAX_RANKING_PAGE + 1}</span>
            <button class="s10-lb-page-btn" id="s10-lb-next" ${rankingPage === MAX_RANKING_PAGE ? "disabled" : ""}>下一页 →</button>
          </div>
        </div>
      </div>

      <!-- 右栏：V3.5.5 战术取景窗（外框固定 + 内部棋盘滚动）-->
      <div class="s10-right">
        <div class="s10-viewport" id="s10-viewport">
          <span class="s10-vp-zone-label">[ 战 区 · BATTLE ZONE ]</span>
          <span class="s10-vp-pager" id="s10-vp-pager">
            <button class="s10-vp-page-btn" id="s10-vp-prev" disabled>◀</button>
            <span class="s10-vp-page-info" id="s10-vp-pageinfo">1 / —</span>
            <button class="s10-vp-page-btn" id="s10-vp-next" disabled>▶</button>
          </span>
          <span class="s10-vp-bracket s10-vp-br-tl"></span>
          <span class="s10-vp-bracket s10-vp-br-tr"></span>
          <span class="s10-vp-bracket s10-vp-br-bl"></span>
          <span class="s10-vp-bracket s10-vp-br-br"></span>
          <div class="s10-map-wrap" id="s10-map-wrap">
            <canvas class="s10-tactical-canvas" id="s10-tactical-canvas"></canvas>
            <div class="s10-level-status" id="s10-level-status" hidden></div>
          </div>
        </div>
      </div>
    </div>
  </div>`;

  bindEvents(container);
}

// ── 排行榜表格 HTML ──
function buildRankingTableHTML(page: number): string {
  const start = page * RANKING_PAGE_SIZE;
  const slice = MOCK_RANKING.slice(start, start + RANKING_PAGE_SIZE);

  return slice.map((entry) => {
    const rankCls = entry.rank <= 3 ? `s10-lb-rank--top${entry.rank}` : "";
    // 通关场数颜色分段
    let clearedColor = "var(--text-muted)";
    if (entry.cleared >= 50) clearedColor = "var(--tactical-cyan)";
    else if (entry.cleared >= 30) clearedColor = "var(--tactical-green)";

    return /* html */ `
    <div class="s10-lb-row">
      <span class="s10-lb-col-rank ${rankCls}">${entry.rank}</span>
      <span class="s10-lb-col-name">${entry.name}</span>
      <span class="s10-lb-col-cleared" style="color:${clearedColor}">${entry.cleared}</span>
    </div>`;
  }).join("");
}

// ── 事件绑定 ──
function bindEvents(container: HTMLElement): void {
  // 共享 header 事件（返回 + 导航按钮）
  bindHeaderEvents(sharpenGo, pathFor("armory-hall"));

  // Tab 切换（身份卡内）
  container.querySelector("#s10-id-tabs")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("[data-mode]") as HTMLElement | null;
    if (!btn) return;
    const mode = btn.dataset.mode as string;
    // 跳过锁定 tab
    if (btn.hasAttribute("disabled")) return;
    if (mode === sharpenState.lastSelectedType) return;
    if (mode !== "1v1" && mode !== "2v2" && mode !== "3v3") return;
    sharpenState.lastSelectedType = mode;
    saveState(sharpenState);
    rankingPage = 0;
    rebuildPage(container);
  });

  // 分页：上一页
  container.querySelector("#s10-lb-prev")?.addEventListener("click", () => {
    if (rankingPage <= 0) return;
    rankingPage--;
    refreshRankingTable(container);
  });

  // 分页：下一页
  container.querySelector("#s10-lb-next")?.addEventListener("click", () => {
    if (rankingPage >= MAX_RANKING_PAGE) return;
    rankingPage++;
    refreshRankingTable(container);
  });

  // V6-011 棋盘翻页
  container.querySelector("#s10-vp-prev")?.addEventListener("click", () => { goToBoardPage(-1); });
  container.querySelector("#s10-vp-next")?.addEventListener("click", () => { goToBoardPage(1); });
}

// ── 排行榜列表刷新（不重建页面，保留 Canvas）──
function refreshRankingTable(container: HTMLElement): void {
  const listEl = container.querySelector("#s10-lb-list");
  const paginationEl = container.querySelector("#s10-lb-pagination");
  if (listEl) {
    listEl.innerHTML = buildRankingTableHTML(rankingPage);
    listEl.scrollTop = 0;
  }
  if (paginationEl) {
    paginationEl.innerHTML = /* html */ `
      <button class="s10-lb-page-btn" id="s10-lb-prev" ${rankingPage === 0 ? "disabled" : ""}>← 上一页</button>
      <span class="s10-lb-page-info">${rankingPage + 1} / ${MAX_RANKING_PAGE + 1}</span>
      <button class="s10-lb-page-btn" id="s10-lb-next" ${rankingPage === MAX_RANKING_PAGE ? "disabled" : ""}>下一页 →</button>
    `;
    // 重新绑定分页按钮
    container.querySelector("#s10-lb-prev")?.addEventListener("click", () => {
      if (rankingPage <= 0) return;
      rankingPage--;
      refreshRankingTable(container);
    });
    container.querySelector("#s10-lb-next")?.addEventListener("click", () => {
      if (rankingPage >= MAX_RANKING_PAGE) return;
      rankingPage++;
      refreshRankingTable(container);
    });
  }
}

// ── 计算棋盘棋子状态 ──
// ── V6-011 棋盘初始化（异步加载关卡数据）──
async function initBoard(): Promise<void> {
  const canvas = document.getElementById("s10-tactical-canvas") as HTMLCanvasElement | null;
  if (!canvas) return;

  const mode = sharpenState.lastSelectedType as OpenType;

  // 加载关卡数据（首次调用 fetch，后续走缓存）
  if (!allLevelsCache) {
    try {
      allLevelsCache = await loadAllLevels();
      // 关卡数据加载完成后刷新身份卡（更新通过 X/总关 数）
      refreshIdentityCard();
    } catch (error) {
      updateLevelStatus(`关卡加载失败：${error instanceof Error ? error.message : String(error)}`, "error");
      return;
    }
  }

  const levels = allLevelsCache[mode] ?? [];
  const loadStatus = getLastLevelLoadStatus();
  if (levels.length === 0) {
    updateLevelStatus(
      loadStatus.status === "error" ? `关卡加载失败：${loadStatus.errorMessage ?? "未知错误"}` : "暂无可用关卡，请检查关卡资源部署。",
      loadStatus.status === "error" ? "error" : "empty",
    );
  } else if (loadStatus.failed > 0) {
    updateLevelStatus(`已加载 ${loadStatus.total} 个关卡，另有 ${loadStatus.failed} 个关卡不可用。`, "warning");
  } else {
    updateLevelStatus("", "ready");
  }
  const progress = sharpenState.typeProgress[mode];

  // P0-01: 根据真实关卡数据填充 rankProgress
  populateRankProgress(levels, progress);

  const clearedSet = new Set(progress.clearedLevels);
  const failedSet = new Set(progress.failedLevels);
  const page = boardPageIdx[mode] ?? 0;
  const boardPage = generateBoardPage(levels, page, clearedSet, failedSet);

  updatePagerUI(boardPage);

  // ★ 强制更新 totalLevels（关卡数据加载后刷新 DOM）
  updateTotalLevels(mode, levels.length);

  if (tacticalMap) {
    tacticalMap.update(boardPage.pieces, [], TYPE_COLORS[mode], [...clearedSet]);
  } else {
    tacticalMap = new TacticalMap({
      canvas,
      pieces: boardPage.pieces,
      links: [],
      themeColor: TYPE_COLORS[mode],
      clearedLevels: [...clearedSet],
      onPieceClick: handlePieceClick,
    });
  }
}

function updateLevelStatus(message: string, status: "ready" | "empty" | "warning" | "error"): void {
  const statusEl = document.getElementById("s10-level-status");
  if (!statusEl) return;
  statusEl.textContent = message;
  statusEl.dataset.status = status;
  statusEl.hidden = status === "ready";
}

// ── V6-011 翻页 UI 更新 ──
function updatePagerUI(boardPage: BoardPage): void {
  const prevBtn = document.getElementById("s10-vp-prev") as HTMLButtonElement | null;
  const nextBtn = document.getElementById("s10-vp-next") as HTMLButtonElement | null;
  const pageInfo = document.getElementById("s10-vp-pageinfo") as HTMLSpanElement | null;

  if (prevBtn) prevBtn.disabled = boardPage.pageIdx <= 0;
  if (nextBtn) nextBtn.disabled = boardPage.pageIdx >= boardPage.totalPages - 1;
  if (pageInfo) pageInfo.textContent = `${boardPage.pageIdx + 1} / ${boardPage.totalPages}`;
}

// ── V6-011 翻页处理 ──
async function goToBoardPage(delta: number): Promise<void> {
  const mode = sharpenState.lastSelectedType as OpenType;
  if (!allLevelsCache) return;

  const levels = allLevelsCache[mode] ?? [];
  const totalPages = computeTotalPages(levels);
  const newPage = Math.min(Math.max(0, (boardPageIdx[mode] ?? 0) + delta), totalPages - 1);
  boardPageIdx[mode] = newPage;

  const progress = sharpenState.typeProgress[mode];
  const clearedSet = new Set(progress.clearedLevels);
  const failedSet = new Set(progress.failedLevels);
  const boardPage = generateBoardPage(levels, newPage, clearedSet, failedSet);
  updatePagerUI(boardPage);

  if (tacticalMap) {
    tacticalMap.update(boardPage.pieces, [], TYPE_COLORS[mode], [...clearedSet]);
  }
}

// ── V6-011 更新身份卡中关卡总数 ──
function updateTotalLevels(_mode: OpenType, total: number): void {
  const totalEl = document.getElementById("s10-identity-total");
  if (totalEl) totalEl.textContent = String(total);
}

function refreshIdentityCard(): void {
  const mode = sharpenState.lastSelectedType as OpenType;
  if (allLevelsCache) {
    updateTotalLevels(mode, allLevelsCache[mode]?.length ?? 0);
  }
  // P0-01/03/05: 同时刷新军衔进度带 + 战报 + 统计
  refreshRankAndBattles();
}

// ── 页面重建 ──
function rebuildPage(container: HTMLElement): void {
  tacticalMap = null;
  // 切 Tab 时重置翻页（不持久化）
  boardPageIdx[sharpenState.lastSelectedType as OpenType] = 0;
  buildDOM(container);
  requestAnimationFrame(() => {
    initBoard();
  });
}

// ── 辅助函数 ──
function calcWinRate(progress: TypeProgress): number {
  if (progress.totalBattles === 0) return 0;
  return Math.round((progress.wins / progress.totalBattles) * 100);
}

/** P0-05 平均胜率：已通关占已挑战的比例 */
function calcAvgWinRate(progress: TypeProgress): number {
  const attempted = new Set([...progress.clearedLevels, ...progress.failedLevels]).size;
  if (attempted === 0) return 0;
  return Math.round((progress.clearedLevels.length / attempted) * 100);
}

/** P0-01 军衔进度带 HTML（4 段） — 已移除（2026-07-25） */

/** P0-03 最近战报（含 PB 对比箭头） */
function renderRecentBattlesHTML(progress: TypeProgress): string {
  const battles = [...progress.recentBattles].reverse().slice(0, 3);
  if (battles.length === 0) {
    return /* html */ `
    <div class="s10-rb-header">RECENT · 暂无战报</div>
    <div class="s10-rb-empty">挑战关卡以生成战报记录</div>`;
  }

  return /* html */ `
  <div class="s10-rb-header">RECENT · 最近战报</div>
  ${battles.map((b) => {
    const resultCls = b.result === "win" ? "s10-rb-win" : "s10-rb-loss";
    const resultText = b.result === "win" ? "胜" : "负";
    const timeSec = (b.timeMs / 1000).toFixed(1);

    // PB 对比箭头
    let hpArrow = "";
    let timeArrow = "";
    if (b.recordBroken.includes("hp")) {
      hpArrow = '<span class="s10-record-arrow s10-record-arrow--hp" title="HP 新纪录">↑NEW</span>';
    }
    if (b.recordBroken.includes("time")) {
      timeArrow = '<span class="s10-record-arrow s10-record-arrow--time" title="用时新纪录">↓NEW</span>';
    }

    return /* html */ `
    <div class="s10-rb-row ${b.isNewRecord ? "s10-rb-row--record" : ""}">
      <span class="s10-rb-result ${resultCls}">${resultText}</span>
      <span class="s10-rb-name">${b.levelName}</span>
      <span class="s10-rb-stat">HP ${b.hpPct}%${hpArrow}</span>
      <span class="s10-rb-stat">${timeSec}s${timeArrow}</span>
      ${b.isRankUp ? '<span class="s10-rb-rankup">▲晋</span>' : ""}
    </div>`;
  }).join("")}`;
}

// ── V6.011 棋子点击处理（inline 战斗观看）──
function handlePieceClick(piece: BoardPiece): void {
  // 地形不可交互；lock 棋子 = 从未挑战，可点击
  if (piece.type === "terrain") {
    console.log(`[sharpen3] 点击地形: ${piece.terrainName ?? piece.name}`);
    return;
  }

  const mode = sharpenState.lastSelectedType as OpenType;
  const levels = allLevelsCache?.[mode];
  if (!levels) return;

  const level = levels.find((l) => l.id === piece.id);
  if (!level) {
    console.warn(`[sharpen3] 找不到关卡: ${piece.id}`);
    return;
  }

  console.log(`[sharpen3] 棋子弹层: ${piece.name} status=${piece.status}`);

  const progress = sharpenState.typeProgress[mode];

  startBattleViewer(
    [level],
    {
      onComplete: (battles) => {
        for (const b of battles) {
          // 更新关卡列表
          if (b.result === "win") {
            if (!progress.clearedLevels.includes(level.id)) {
              progress.clearedLevels.push(level.id);
            }
            const failIdx = progress.failedLevels.indexOf(level.id);
            if (failIdx !== -1) progress.failedLevels.splice(failIdx, 1);
          } else {
            if (!progress.failedLevels.includes(level.id)) {
              progress.failedLevels.push(level.id);
            }
          }
          // 生成战斗记录（含 PB 对比、晋升判定）
          const record = createBattleRecord(level, b.result, b.hpPct, b.timeMs, progress);
          saveState(sharpenState);
          // 刷新棋盘 + 军衔进度带 + 战报
          refreshBoardPage();
          refreshRankAndBattles();
          // 段位晋升仪式 or 新纪录弹窗
          if (record.isRankUp || record.isNewRecord) {
            showCeremony(record);
          }
        }
      },
      onSkip: () => {
        console.log("[sharpen3] 战斗观看已跳过");
      },
    }
  );
}

async function refreshBoardPage(): Promise<void> {
  if (!tacticalMap) return;
  const mode = sharpenState.lastSelectedType as OpenType;
  const levels = allLevelsCache?.[mode] ?? [];
  const progress = sharpenState.typeProgress[mode];
  const clearedSet = new Set(progress.clearedLevels);
  const failedSet = new Set(progress.failedLevels);
  const page = boardPageIdx[mode] ?? 0;
  const boardPage = generateBoardPage(levels, page, clearedSet, failedSet);
  updatePagerUI(boardPage);
  tacticalMap.update(boardPage.pieces, [], TYPE_COLORS[mode], [...clearedSet]);
  // 刷新军衔进度带 + 战报
  refreshRankAndBattles();
}

// ── P0-01 根据真实关卡数据填充 rankProgress ──
function populateRankProgress(levels: LevelEntry[], progress: TypeProgress): void {
  const clearedSet = new Set(progress.clearedLevels);
  for (const cfg of RANK_CONFIGS) {
    const rankLevels = levels.filter((l) => l.rank === cfg.id);
    const cleared = rankLevels.filter((l) => clearedSet.has(l.id)).length;
    progress.rankProgress[cfg.id] = {
      cleared,
      total: rankLevels.length || cfg.floorCount,
      battles: progress.rankProgress[cfg.id]?.battles ?? 0,
    };
  }
}

// ── P0-02 段位晋升判定 ──
function checkRankPromotion(progress: TypeProgress): { promoted: boolean; fromRank: RankId; toRank: RankId } | null {
  const currentIdx = RANK_CONFIGS.findIndex((r) => r.id === progress.currentRank);
  if (currentIdx < 0 || currentIdx >= RANK_CONFIGS.length - 1) return null;

  const current = RANK_CONFIGS[currentIdx];
  const rp = progress.rankProgress[current.id];
  if (!rp || rp.total === 0) return null;
  if (rp.cleared / rp.total < RANK_PROMOTION_THRESHOLD) return null;

  const next = RANK_CONFIGS[currentIdx + 1];
  return { promoted: true, fromRank: current.id, toRank: next.id };
}

// ── P0-04 生成战斗记录（含 PB 对比）──
function createBattleRecord(
  level: LevelEntry, result: "win" | "loss", hpPct: number, timeMs: number, progress: TypeProgress
): BattleRecord {
  const pb = progress.pbMap[level.id];
  const recordBroken: RecordField[] = [];

  if (result === "win") {
    if (!pb || hpPct > pb.bestHpPct) recordBroken.push("hp");
    if (!pb || timeMs < pb.bestTimeMs) recordBroken.push("time");
    if ((progress.personalBests?.longestStreak ?? 0) > (pb?.longestStreak ?? 0)) recordBroken.push("streak");
  }

  // 更新 PB
  if (!pb) {
    progress.pbMap[level.id] = {
      bestHpPct: hpPct,
      bestTimeMs: result === "win" ? timeMs : 999999,
      longestStreak: progress.personalBests?.currentStreak ?? 0,
      firstClearAt: result === "win" ? Date.now() : 0,
      clearCount: result === "win" ? 1 : 0,
    };
  } else {
    if (hpPct > pb.bestHpPct) pb.bestHpPct = hpPct;
    if (result === "win" && timeMs < pb.bestTimeMs) pb.bestTimeMs = timeMs;
    if ((progress.personalBests?.currentStreak ?? 0) > pb.longestStreak) {
      pb.longestStreak = progress.personalBests?.currentStreak ?? 0;
    }
    if (result === "win") {
      if (pb.firstClearAt === 0) pb.firstClearAt = Date.now();
      pb.clearCount++;
    }
  }

  // 连胜更新
  if (!progress.personalBests) {
    progress.personalBests = { longestStreak: 0, currentStreak: 0 };
  }
  if (result === "win") {
    progress.personalBests.currentStreak++;
    if (progress.personalBests.currentStreak > progress.personalBests.longestStreak) {
      progress.personalBests.longestStreak = progress.personalBests.currentStreak;
    }
  } else {
    progress.personalBests.currentStreak = 0;
  }

  // 推进等级经验
  if (result === "win") {
    progress.exp++;
    progress.wins++;
  } else {
    progress.losses++;
  }
  progress.totalBattles++;

  // rankProgress 更新
  const rankEntry = progress.rankProgress[level.rank];
  if (rankEntry) rankEntry.battles++;

  // 晋升判定
  const promotion = checkRankPromotion(progress);

  const record: BattleRecord = {
    levelId: level.id,
    levelName: level.name,
    aiType: level.difficulty,
    result,
    hpPct,
    timeMs,
    exp: result === "win" ? 1 : 0,
    timestamp: Date.now(),
    rank: level.rank,
    isNewRecord: recordBroken.length > 0,
    recordBroken,
    isRankUp: !!promotion,
  };

  // 最近战报（保留最近 3 场）
  progress.recentBattles.push(record);
  if (progress.recentBattles.length > 3) {
    progress.recentBattles = progress.recentBattles.slice(-3);
  }

  // 段位晋升
  if (promotion) {
    progress.currentRank = promotion.toRank;
  }

  return record;
}

// ── P0-02 段位晋升仪式弹窗 ──
function showCeremony(record: BattleRecord): void {
  const mode = sharpenState.lastSelectedType as OpenType;
  const currentRank = sharpenState.typeProgress[mode].currentRank;
  const toCfg = RANK_CONFIGS.find((r) => r.id === currentRank);

  const overlay = document.createElement("div");
  overlay.className = "s10-ceremony-overlay";

  let contentHTML = "";
  if (record.isRankUp && toCfg) {
    const prevCfg = RANK_CONFIGS[toCfg.index - 1] ?? RANK_CONFIGS[0];
    contentHTML = /* html */ `
    <div class="s10-ceremony">
      <div class="s10-ceremony-icon">⚔</div>
      <div class="s10-ceremony-title">段 位 晋 升</div>
      <div class="s10-ceremony-ranks">
        <span class="s10-ceremony-from">${prevCfg.displayName}</span>
        <span class="s10-ceremony-arrow">▸</span>
        <span class="s10-ceremony-to">${toCfg.displayName}</span>
      </div>
      <div class="s10-ceremony-flavor">${getCeremonyFlavor(toCfg.id)}</div>
    </div>`;
  } else if (record.isNewRecord) {
    const records: { label: string; oldVal: string; newVal: string }[] = [];
    if (record.recordBroken.includes("hp")) {
      records.push({ label: "HP%", oldVal: "—", newVal: `${record.hpPct}%` });
    }
    if (record.recordBroken.includes("time")) {
      records.push({ label: "用时", oldVal: "—", newVal: `${(record.timeMs / 1000).toFixed(1)}s` });
    }
    if (record.recordBroken.includes("streak")) {
      records.push({ label: "连胜", oldVal: "—", newVal: `${sharpenState.typeProgress[sharpenState.lastSelectedType as OpenType].personalBests?.currentStreak ?? 0}` });
    }
    contentHTML = /* html */ `
    <div class="s10-ceremony">
      <div class="s10-ceremony-icon">★</div>
      <div class="s10-ceremony-title">新 纪 录</div>
      <div class="s10-ceremony-level">${record.levelName}</div>
      <div class="s10-record-break-cards">
        ${records.map((r) => /* html */ `
        <div class="s10-record-break-card">
          <span class="s10-rbc-label">${r.label}</span>
          <span class="s10-rbc-old">${r.oldVal}</span>
          <span class="s10-rbc-arrow">▸</span>
          <span class="s10-rbc-new">${r.newVal}</span>
        </div>`).join("")}
      </div>
    </div>`;
  }

  overlay.innerHTML = contentHTML;
  document.body.appendChild(overlay);

  // 1.2s 后渐隐
  setTimeout(() => {
    overlay.classList.add("s10-ceremony-overlay--fading");
    setTimeout(() => overlay.remove(), 500);
  }, 1500);
}

function getCeremonyFlavor(rankId: RankId): string {
  switch (rankId) {
    case "soldier": return "战士起步碎星光";
    case "officer": return "校尉将帅踏沧浪";
    case "field_officer": return "莫问峰高天几许";
    case "general": return "不破楼兰剑不藏";
    default: return "";
  }
}

// ── 刷新军衔进度带 + 战报（独立增量刷新，不重建整个 DOM）──
function refreshRankAndBattles(): void {
  const mode = sharpenState.lastSelectedType as OpenType;
  const progress = sharpenState.typeProgress[mode];
  const levels = allLevelsCache?.[mode] ?? [];

  // 刷新最近战报
  const battlesEl = document.getElementById("s10-recent-battles");
  if (battlesEl) {
    battlesEl.innerHTML = renderRecentBattlesHTML(progress);
  }

  // 刷新统计行
  const statsRow = document.getElementById("s10-stats-row");
  if (statsRow) {
    statsRow.innerHTML = /* html */ `
      <span>通过 <b>${progress.clearedLevels.length}</b>/<b id="s10-identity-total">${levels.length || "—"}</b></span>
      <span>胜率 <b>${calcWinRate(progress)}%</b></span>
      <span>连胜 <b>${progress.personalBests?.currentStreak ?? 0}</b></span>
      <span>最长连胜 <b>${progress.personalBests?.longestStreak ?? 0}</b></span>
      <span>均胜率 <b>${calcAvgWinRate(progress)}%</b></span>`;
  }
}
